import React, { useState, useEffect, useRef } from 'react';
import { QRCodeService } from '../../lib/services/qrCodeService';
import { ClipboardUtils } from '../../lib/utils/clipboardUtils';
import { ErrorHandler } from '../../lib/utils/errorHandler';
import { EncoderToolType } from '../../lib/types';
import './QRCode.css';

export const QRCode: React.FC = () => {
  const [text, setText] = useState('');
  const [size, setSize] = useState(200);
  const [errorCorrectionLevel, setErrorCorrectionLevel] = useState<'L' | 'M' | 'Q' | 'H'>('M');
  const [exportFormat, setExportFormat] = useState<'PNG' | 'SVG' | 'JPG'>('PNG');
  const [qrCodeData, setQrCodeData] = useState<string>('');
  const [scanResult, setScanResult] = useState<string>('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [copyFeedback, setCopyFeedback] = useState<'idle' | 'copied' | 'error'>('idle');
  const [mode, setMode] = useState<'generate' | 'scan'>('generate');
  const fileInputRef = useRef<HTMLInputElement>(null);
  const processingRef = useRef<string>('');

  const handleGenerate = async () => {
    if (!text.trim()) {
      setQrCodeData('');
      setError(null);
      return;
    }

    setIsLoading(true);
    setError(null);
    
    const processingKey = `generate-${text}-${size}-${errorCorrectionLevel}-${exportFormat}`;
    processingRef.current = processingKey;

    try {
      const config = QRCodeService.createConfiguration(
        text,
        {
          size,
          errorCorrectionLevel,
          exportFormat
        }
      );
      
      const result = await QRCodeService.generate(config);

      if (processingRef.current === processingKey) {
        if (result.status === 'success') {
          setQrCodeData(result.outputData as string);
          setError(null);
        } else {
          setError(result.errorMessage || 'Generation failed');
          setQrCodeData('');
        }
      }
    } catch (err) {
      if (processingRef.current === processingKey) {
        const errorMessage = ErrorHandler.handle(err, EncoderToolType.QR_CODE);
        setError(errorMessage);
        setQrCodeData('');
      }
    } finally {
      if (processingRef.current === processingKey) {
        setIsLoading(false);
      }
    }
  };

  const handleScan = async (file: File) => {
    if (!file) return;

    setIsLoading(true);
    setError(null);
    setScanResult('');

    const processingKey = `scan-${file.name}-${file.size}`;
    processingRef.current = processingKey;

    try {
      // Convert to standard format and fix orientation for mobile captures
      const processedFile = await processMobileImage(file);

      const result = await QRCodeService.scan(processedFile);

      if (processingRef.current === processingKey) {
        if (result.status === 'success') {
          setScanResult(result.outputData as string);
          setError(null);
        } else {
          setError(result.errorMessage || 'Scanning failed');
          setScanResult('');
        }
      }
    } catch (err) {
      if (processingRef.current === processingKey) {
        const errorMessage = ErrorHandler.handle(err, EncoderToolType.QR_CODE);
        setError(errorMessage);
        setScanResult('');
      }
    } finally {
      if (processingRef.current === processingKey) {
        setIsLoading(false);
      }
    }
  };

  const processMobileImage = async (file: File): Promise<File> => {
    return new Promise((resolve) => {
      const reader = new FileReader();
      reader.onload = (e) => {
        const img = new Image();
        img.onload = () => {
          const canvas = document.createElement('canvas');
          const ctx = canvas.getContext('2d');
          if (!ctx) {
            resolve(file);
            return;
          }

          // Fix orientation and resize for mobile captures
          const MAX_DIMENSION = 1024;
          let width = img.width;
          let height = img.height;

          // Calculate scaling for large images
          const scale = Math.min(1, MAX_DIMENSION / Math.max(width, height));
          const scaledWidth = Math.floor(width * scale);
          const scaledHeight = Math.floor(height * scale);

          let dx = 0;
          let dy = 0;
          let dw = width;
          let dh = height;

          // Simple approach: don't rotate, just resize
          canvas.width = scaledWidth;
          canvas.height = scaledHeight;

          ctx.drawImage(img, 0, 0, width, height, dx, dy, dw, dh);

          canvas.toBlob((blob) => {
            if (blob) {
              resolve(new File([blob], file.name, { type: 'image/png' }));
            } else {
              resolve(file);
            }
          }, 'image/png');
        };
        img.src = e.target?.result as string;
      };
      reader.readAsDataURL(file);
    });
  };

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      handleScan(file);
    }
  };

  const handleCopyQRCode = async () => {
    if (!qrCodeData) return;

    try {
      let dataToCopy: string;
      
      if (exportFormat === 'SVG') {
        dataToCopy = qrCodeData;
      } else {
        // For PNG/JPG, qrCodeData is base64 image data
        dataToCopy = qrCodeData;
      }

      const success = await ClipboardUtils.copyText(dataToCopy);
      if (success) {
        setCopyFeedback('copied');
        setTimeout(() => setCopyFeedback('idle'), 2000);
      } else {
        setCopyFeedback('error');
        setTimeout(() => setCopyFeedback('idle'), 2000);
      }
    } catch (error) {
      console.error('Copy error:', error);
      setCopyFeedback('error');
      setTimeout(() => setCopyFeedback('idle'), 2000);
    }
  };

  const handleCopyScanResult = async () => {
    if (!scanResult) return;

    try {
      const success = await ClipboardUtils.copyText(scanResult);
      if (success) {
        setCopyFeedback('copied');
        setTimeout(() => setCopyFeedback('idle'), 2000);
      } else {
        setCopyFeedback('error');
        setTimeout(() => setCopyFeedback('idle'), 2000);
      }
    } catch (error) {
      console.error('Copy error:', error);
      setCopyFeedback('error');
      setTimeout(() => setCopyFeedback('idle'), 2000);
    }
  };

  const getButtonText = (type: 'qrcode' | 'scanresult') => {
    switch (copyFeedback) {
      case 'copied':
        return 'Copied!';
      case 'error':
        return 'Failed!';
      default:
        return type === 'qrcode' ? 'Copy QR Code' : 'Copy Result';
    }
  };

  const getButtonClassName = () => {
    const baseClass = 'copy-button';
    const hasData = mode === 'generate' ? qrCodeData : scanResult;
    if (!hasData) return `${baseClass} copy-idle`;
    
    switch (copyFeedback) {
      case 'copied':
        return `${baseClass} copy-copied`;
      case 'error':
        return `${baseClass} copy-error`;
      default:
        return `${baseClass} copy-idle`;
    }
  };

  const downloadQRCode = () => {
    if (!qrCodeData) return;

    try {
      let mimeType: string;
      let fileName: string;
      let data: string;

      if (exportFormat === 'SVG') {
        mimeType = 'image/svg+xml';
        fileName = `qrcode.svg`;
        data = qrCodeData;
      } else {
        mimeType = exportFormat === 'PNG' ? 'image/png' : 'image/jpeg';
        fileName = `qrcode.${exportFormat.toLowerCase()}`;
        data = qrCodeData.startsWith('data:') ? qrCodeData : `data:${mimeType};base64,${qrCodeData}`;
      }

      const link = document.createElement('a');
      link.href = data;
      link.download = fileName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (error) {
      console.error('Download error:', error);
      setError('Failed to download QR code');
    }
  };

  useEffect(() => {
    if (text && mode === 'generate') {
      const timer = setTimeout(() => {
        handleGenerate();
      }, 300);

      return () => clearTimeout(timer);
    } else {
      setQrCodeData('');
      setError(null);
      setIsLoading(false);
    }
  }, [text, size, errorCorrectionLevel, exportFormat, mode]);

  return (
    <div className="qr-code">
      <div style={{ marginBottom: '2rem' }}>
        <h1>QR Code Generator and Scanner</h1>
        <p>
          Generate QR codes from text or scan QR codes from images.
          Supports multiple export formats and error correction levels.
        </p>
      </div>

      <div className="qr-container">
        <div className="form-group">
          <label>
            Mode:
          </label>
          <select
            value={mode}
            onChange={(e) => setMode(e.target.value as 'generate' | 'scan')}
          >
            <option value="generate">Generate QR Code</option>
            <option value="scan">Scan QR Code</option>
          </select>
        </div>

        {mode === 'generate' ? (
          <>
            <div className="form-group">
              <label>
                Text Content:
              </label>
              <textarea
                value={text}
                onChange={(e) => setText(e.target.value)}
                placeholder="Enter text to encode in QR code..."
                rows={3}
                data-testid="qr-input"
              />
            </div>

            <div className="form-row">
              <div className="form-group">
                <label>
                  Size (pixels):
                </label>
                <input
                  type="number"
                  min="100"
                  max="500"
                  value={size}
                  onChange={(e) => setSize(Number(e.target.value))}
                />
              </div>

              <div className="form-group">
                <label>
                  Error Correction:
                </label>
                <select
                  value={errorCorrectionLevel}
                  onChange={(e) => setErrorCorrectionLevel(e.target.value as 'L' | 'M' | 'Q' | 'H')}
                >
                  <option value="L">Low (7%)</option>
                  <option value="M">Medium (15%)</option>
                  <option value="Q">Quartile (25%)</option>
                  <option value="H">High (30%)</option>
                </select>
              </div>

              <div className="form-group">
                <label>
                  Export Format:
                </label>
                <select
                  value={exportFormat}
                  onChange={(e) => setExportFormat(e.target.value as 'PNG' | 'SVG' | 'JPG')}
                  data-testid="export-format-select"
                >
                  <option value="PNG">PNG</option>
                  <option value="SVG">SVG</option>
                  <option value="JPG">JPG</option>
                </select>
              </div>
            </div>

            {error && (
              <div className="status-message status-error">
                {error}
              </div>
            )}

            <div className="qr-output">
              <label>
                Generated QR Code:
              </label>
              {qrCodeData ? (
                <div className="qr-preview">
                  {exportFormat === 'SVG' ? (
                    <div 
                      dangerouslySetInnerHTML={{ __html: qrCodeData }}
                      className="qr-svg"
                    />
                  ) : (
                    <img 
                      src={qrCodeData.startsWith('data:') ? qrCodeData : `data:image/${exportFormat.toLowerCase()};base64,${qrCodeData}`}
                      alt="Generated QR Code"
                      className="qr-image"
                    />
                  )}
                </div>
              ) : (
                <div className="qr-placeholder">
                  {isLoading ? 'Generating...' : 'QR code will appear here...'}
                </div>
              )}
            </div>

            <div className="button-container">
              <button
                onClick={handleCopyQRCode}
                disabled={!qrCodeData}
                className={getButtonClassName()}
              >
                {getButtonText('qrcode')}
              </button>
              <button
                onClick={downloadQRCode}
                disabled={!qrCodeData}
                className="download-button"
              >
                Download
              </button>
            </div>
          </>
        ) : (
          <>
            <div className="form-group">
              <label htmlFor="qr-file-upload">
                Upload QR Code Image:
              </label>
              <input
                id="qr-file-upload"
                ref={fileInputRef}
                type="file"
                accept="image/*"
                onChange={handleFileSelect}
                className="file-input"
                data-testid="file-input"
              />
              <p className="file-input-hint">
                Upload QR code images or take photos. Supported formats: PNG, JPG, SVG, and other image formats
              </p>
            </div>

            {error && (
              <div className="status-message status-error">
                {error}
              </div>
            )}

            {scanResult && (
              <div className="scan-result">
                <label>
                  Scanned Content:
                </label>
                <div className="scan-text">
                  {scanResult}
                </div>
              </div>
            )}

            <div className="button-container">
              <button
                onClick={handleCopyScanResult}
                disabled={!scanResult}
                className={getButtonClassName()}
              >
                {getButtonText('scanresult')}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};